import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  Link,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Checkbox,
  FormControlLabel,
  Grid,
  Tabs,
  Tab,
  Autocomplete,
  Chip,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Lock,
  Person,
  Business,
  Description,
  Search,
  Email,
  CheckCircle,
  Send,
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useNavigate, useSearchParams, Link as RouterLink } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import { toast } from 'react-toastify';

import { registerUser, registerSeller, clearError, clearRegistrationSuccess, sendEmailOtp, verifyEmailOtp } from '../../store/slices/authSlice';

// Validation schemas
const customerSchema = yup.object({
  firstName: yup.string().required('First name is required'),
  lastName: yup.string().required('Last name is required'),
  email: yup.string().email('Please enter a valid email').required('Email is required'),
  phoneNumber: yup.string().required('Phone number is required'),
  password: yup.string().min(6, 'Password must be at least 6 characters').required('Password is required'),
  confirmPassword: yup.string().oneOf([yup.ref('password')], 'Passwords must match').required('Please confirm your password'),
  agreeToTerms: yup.boolean().oneOf([true], 'You must agree to the terms and conditions'),
});

const sellerSchema = yup.object({
  firstName: yup.string().required('First name is required'),
  lastName: yup.string().required('Last name is required'),
  email: yup.string().email('Please enter a valid email').required('Email is required'),
  phoneNumber: yup.string().required('Phone number is required'),
  password: yup.string().min(6, 'Password must be at least 6 characters').required('Password is required'),
  confirmPassword: yup.string().oneOf([yup.ref('password')], 'Passwords must match').required('Please confirm your password'),
  businessName: yup.string().required('Business name is required'),
  businessType: yup.string().required('Business type is required'),
  gstNumber: yup.string().required('GST number is required'),
  panNumber: yup.string().required('PAN number is required'),
  businessAddress: yup.string().required('Business address is required'),
  city: yup.string().required('City is required'),
  state: yup.string().required('State is required'),
  pincode: yup.string().required('Pincode is required'),
  bankAccountName: yup.string().nullable().notRequired(),
  bankAccountNumber: yup.string().nullable().notRequired().test(
    'account-number-format',
    'Account number should be 9-18 digits',
    function(value) {
      if (!value || value.length === 0) return true; // Optional field
      return /^[0-9]{9,18}$/.test(value);
    }
  ),
  ifscCode: yup.string().nullable().notRequired().test(
    'ifsc-format',
    'Invalid IFSC code format',
    function(value) {
      if (!value || value.length === 0) return true; // Optional field
      return /^[A-Z]{4}0[A-Z0-9]{6}$/.test(value.toUpperCase());
    }
  ),
  agreeToTerms: yup.boolean().oneOf([true], 'You must agree to the terms and conditions'),
});

const RegisterPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();

  const { loading, error, registrationSuccess } = useSelector((state) => state.auth);

  const [userType, setUserType] = useState(searchParams.get('type') === 'seller' ? 'seller' : 'customer');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [stateSearchTerm, setStateSearchTerm] = useState('');
  const [customStateValue, setCustomStateValue] = useState('');
  const [isCustomState, setIsCustomState] = useState(false);

  // Email OTP state
  const [emailOtpSent, setEmailOtpSent] = useState(false);
  const [emailVerified, setEmailVerified] = useState(false);
  const [otpValue, setOtpValue] = useState('');
  const [otpError, setOtpError] = useState('');
  const [otpLoading, setOtpLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const timerRef = useRef(null);

  const schema = userType === 'seller' ? sellerSchema : customerSchema;
  
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phoneNumber: '',
      password: '',
      confirmPassword: '',
      businessName: '',
      businessType: '',
      gstNumber: '',
      panNumber: '',
      businessAddress: '',
      city: '',
      state: '',
      pincode: '',
      bankAccountName: '',
      bankAccountNumber: '',
      ifscCode: '',
      agreeToTerms: false,
    },
  });
  
  useEffect(() => {
    dispatch(clearError());
    dispatch(clearRegistrationSuccess());
  }, [dispatch]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, []);

  const startResendCooldown = () => {
    setResendCooldown(60);
    timerRef.current = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) { clearInterval(timerRef.current); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  const handleSendOtp = async (emailVal) => {
    if (!emailVal || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailVal)) {
      toast.error('Please enter a valid email address first');
      return;
    }
    setOtpLoading(true);
    try {
      await dispatch(sendEmailOtp(emailVal)).unwrap();
      setEmailOtpSent(true);
      setOtpValue('');
      setOtpError('');
      startResendCooldown();
      toast.success('OTP sent to your email address');
    } catch (e) {
      toast.error(e || 'Failed to send OTP');
    } finally {
      setOtpLoading(false);
    }
  };

  const handleVerifyOtp = async (emailVal) => {
    if (!otpValue || otpValue.length !== 6) {
      setOtpError('Please enter the 6-digit OTP');
      return;
    }
    setOtpLoading(true);
    setOtpError('');
    try {
      await dispatch(verifyEmailOtp({ email: emailVal, otp: otpValue })).unwrap();
      setEmailVerified(true);
      toast.success('Email verified successfully!');
    } catch (e) {
      setOtpError(e || 'Invalid OTP. Please try again.');
    } finally {
      setOtpLoading(false);
    }
  };
  
  // Show success message and redirect
  useEffect(() => {
    if (registrationSuccess) {
      toast.success(
        userType === 'seller' 
          ? 'Seller registration successful! Please wait for admin approval.'
          : 'Registration successful! Please check your email for verification.'
      );
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    }
  }, [registrationSuccess, userType, navigate]);
  
  // Show error toast
  useEffect(() => {
    if (error) {
      toast.error(error);
    }
  }, [error]);
  
  // Reset form and OTP state when user type changes
  useEffect(() => {
    reset();
    setIsCustomState(false);
    setCustomStateValue('');
    setStateSearchTerm('');
    setEmailOtpSent(false);
    setEmailVerified(false);
    setOtpValue('');
    setOtpError('');
    setResendCooldown(0);
    if (timerRef.current) clearInterval(timerRef.current);
  }, [userType, reset]);
  
  const onSubmit = async (data) => {
    if (!emailVerified) {
      toast.error('Please verify your email address before registering');
      return;
    }
    try {
      if (userType === 'seller') {
        await dispatch(registerSeller({
          ...data,
          role: 'SELLER',
          businessProfile: {
            businessName: data.businessName,
            businessType: data.businessType,
            gstNumber: data.gstNumber,
            panNumber: data.panNumber,
            businessAddress: {
              street: data.businessAddress,
              city: data.city,
              state: data.state,
              postalCode: data.pincode,
              country: data.country || 'India',
            },
            bankDetails: (data.bankAccountName?.trim() || data.bankAccountNumber?.trim() || data.ifscCode?.trim()) ? {
              accountHolderName: data.bankAccountName?.trim() || null,
              accountNumber: data.bankAccountNumber?.trim() || null,
              ifscCode: data.ifscCode?.trim() ? data.ifscCode.trim().toUpperCase() : null,
            } : null,
          },
        })).unwrap();
      } else {
        await dispatch(registerUser({
          ...data,
          role: 'CUSTOMER',
        })).unwrap();
      }
    } catch (error) {
      // Error is handled by the error effect above
    }
  };
  
  const handleUserTypeChange = (event, newValue) => {
    setUserType(newValue);
  };
  
  const businessTypes = [
    'Manufacturer',
    'Distributor',
    'Retailer',
    'Wholesaler',
    'Importer',
    'Exporter',
    'Service Provider',
  ];
  
  const indianStates = [
    'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
    'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka',
    'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram',
    'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu',
    'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
    'Delhi', 'Jammu and Kashmir', 'Ladakh', 'Puducherry',
  ];
  
  const CUSTOM_STATE_OPTION = '__CUSTOM__';
  
  // Filter states based on search term
  const filteredStates = indianStates.filter(state =>
    state.toLowerCase().includes(stateSearchTerm.toLowerCase())
  );
  
  return (
    <Container maxWidth="md" sx={{ py: 6 }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <Paper
          elevation={8}
          sx={{
            p: 4,
            borderRadius: 3,
            background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
          }}
        >
          {/* Header */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography
              variant="h3"
              component="h1"
              sx={{
                fontWeight: 700,
                mb: 1,
                background: 'linear-gradient(45deg, #2C3E50, #E67E22)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Join MOS Marketplace
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Create your account to start buying or selling premium building materials
            </Typography>
          </Box>
          
          {/* User Type Tabs */}
          <Box sx={{ mb: 4 }}>
            <Tabs
              value={userType}
              onChange={handleUserTypeChange}
              centered
              sx={{
                '& .MuiTabs-indicator': {
                  backgroundColor: 'primary.main',
                },
              }}
            >
              <Tab
                label="Customer Registration"
                value="customer"
                icon={<Person />}
                iconPosition="start"
                sx={{ minHeight: 60, fontSize: '1rem' }}
              />
              <Tab
                label="Seller Registration"
                value="seller"
                icon={<Business />}
                iconPosition="start"
                sx={{ minHeight: 60, fontSize: '1rem' }}
              />
            </Tabs>
          </Box>
          
          {/* Error Alert */}
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}
          
          {/* Registration Form */}
          <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
            <Grid container spacing={2.5}>
              {/* Personal Details Section */}
              <Grid item xs={12}>
                <Paper 
                  elevation={1} 
                  sx={{ 
                    p: 2, 
                    mb: 0, 
                    border: '1px solid #e0e0e0',
                    backgroundColor: '#ffffff',
                    borderRadius: 1
                  }}
                >
                  <Typography 
                    variant="subtitle1" 
                    sx={{ 
                      mb: 1.5, 
                      color: '#333333',
                      fontWeight: 600,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      fontSize: '1rem'
                    }}
                  >
                  <Person sx={{ fontSize: 18 }} />
                  Personal Details
                </Typography>
                
                <Grid container spacing={1.5}>
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="firstName"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label="First Name"
                          error={!!errors.firstName}
                          helperText={errors.firstName?.message}
                          variant="outlined"
                          size="small"
                          sx={{ 
                            '& .MuiInputBase-root': { height: '36px' },
                            '& .MuiFormHelperText-root': { margin: '2px 0 0 0', fontSize: '0.75rem' }
                          }}
                        />
                      )}
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="lastName"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label="Last Name"
                          error={!!errors.lastName}
                          helperText={errors.lastName?.message}
                          variant="outlined"
                          size="small"
                          sx={{ 
                            '& .MuiInputBase-root': { height: '36px' },
                            '& .MuiFormHelperText-root': { margin: '2px 0 0 0', fontSize: '0.75rem' }
                          }}
                        />
                      )}
                    />
                  </Grid>
                  
                  {/* Email + OTP Verification */}
                  <Grid item xs={12}>
                    <Controller
                      name="email"
                      control={control}
                      render={({ field }) => (
                        <Box>
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                            <TextField
                              {...field}
                              fullWidth
                              label="Email"
                              type="email"
                              error={!!errors.email}
                              helperText={errors.email?.message}
                              variant="outlined"
                              size="small"
                              disabled={emailVerified}
                              onChange={(e) => {
                                field.onChange(e);
                                // reset OTP state if email changes after OTP was sent
                                if (emailOtpSent || emailVerified) {
                                  setEmailOtpSent(false);
                                  setEmailVerified(false);
                                  setOtpValue('');
                                  setOtpError('');
                                  setResendCooldown(0);
                                  if (timerRef.current) clearInterval(timerRef.current);
                                }
                              }}
                              sx={{
                                '& .MuiInputBase-root': { height: '36px' },
                                '& .MuiFormHelperText-root': { margin: '2px 0 0 0', fontSize: '0.75rem' }
                              }}
                              InputProps={{
                                startAdornment: <InputAdornment position="start"><Email fontSize="small" color={emailVerified ? 'success' : 'action'} /></InputAdornment>,
                                endAdornment: emailVerified && (
                                  <InputAdornment position="end">
                                    <Chip label="Verified" color="success" size="small" icon={<CheckCircle />} />
                                  </InputAdornment>
                                ),
                              }}
                            />
                            {!emailVerified && (
                              <Button
                                variant="outlined"
                                size="small"
                                disabled={otpLoading || (resendCooldown > 0 && emailOtpSent)}
                                onClick={() => handleSendOtp(field.value)}
                                startIcon={otpLoading ? <CircularProgress size={14} /> : <Send fontSize="small" />}
                                sx={{ whiteSpace: 'nowrap', minWidth: 110, height: '36px', flexShrink: 0 }}
                              >
                                {emailOtpSent
                                  ? resendCooldown > 0 ? `${resendCooldown}s` : 'Resend OTP'
                                  : 'Send OTP'}
                              </Button>
                            )}
                          </Box>

                          {/* OTP Input */}
                          {emailOtpSent && !emailVerified && (
                            <Box sx={{ mt: 1.5, display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                              <TextField
                                fullWidth
                                label="Enter 6-digit OTP"
                                value={otpValue}
                                onChange={(e) => { setOtpValue(e.target.value.replace(/\D/g, '').slice(0, 6)); setOtpError(''); }}
                                error={!!otpError}
                                helperText={otpError || 'Check your email inbox for the OTP'}
                                variant="outlined"
                                size="small"
                                inputProps={{ maxLength: 6, style: { letterSpacing: 6, fontWeight: 700 } }}
                                sx={{
                                  '& .MuiInputBase-root': { height: '36px' },
                                  '& .MuiFormHelperText-root': { margin: '2px 0 0 0', fontSize: '0.75rem' }
                                }}
                              />
                              <Button
                                variant="contained"
                                size="small"
                                disabled={otpLoading || otpValue.length !== 6}
                                onClick={() => handleVerifyOtp(field.value)}
                                sx={{ height: '36px', flexShrink: 0, minWidth: 100, background: '#27AE60', '&:hover': { background: '#229954' } }}
                              >
                                {otpLoading ? <CircularProgress size={16} color="inherit" /> : 'Verify OTP'}
                              </Button>
                            </Box>
                          )}

                          {emailVerified && (
                            <Alert severity="success" sx={{ mt: 1, py: 0.25, fontSize: '0.8rem' }}>
                              Email verified successfully!
                            </Alert>
                          )}
                        </Box>
                      )}
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="phoneNumber"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label="Phone Number"
                          error={!!errors.phoneNumber}
                          helperText={errors.phoneNumber?.message}
                          variant="outlined"
                          size="small"
                          sx={{ 
                            '& .MuiInputBase-root': { height: '36px' },
                            '& .MuiFormHelperText-root': { margin: '2px 0 0 0', fontSize: '0.75rem' }
                          }}
                        />
                      )}
                    />
                  </Grid>
                </Grid>
                </Paper>
              </Grid>
              
              {/* Password Settings Section */}
              <Grid item xs={12}>
                <Paper 
                  elevation={1} 
                  sx={{ 
                    p: 2, 
                    mb: 2.5, 
                    border: '1px solid #e0e0e0',
                    backgroundColor: '#ffffff',
                    borderRadius: 1
                  }}
                >
                <Typography 
                  variant="subtitle1" 
                  sx={{ 
                    mb: 1.5, 
                    color: '#333333',
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    fontSize: '1rem'
                  }}
                >
                  <Lock sx={{ fontSize: 18 }} />
                  Password Settings
                </Typography>
                
                <Grid container spacing={1.5}>
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="password"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label="Password"
                          type={showPassword ? 'text' : 'password'}
                          error={!!errors.password}
                          helperText={errors.password?.message}
                          variant="outlined"
                          size="small"
                          sx={{ 
                            '& .MuiInputBase-root': { height: '36px' },
                            '& .MuiFormHelperText-root': { margin: '2px 0 0 0', fontSize: '0.75rem' }
                          }}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  onClick={() => setShowPassword(!showPassword)}
                                  edge="end"
                                  size="small"
                                  sx={{ p: 0.5 }}
                                >
                                  {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                      />
                    )}
                  />
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="confirmPassword"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label="Confirm Password"
                          type={showConfirmPassword ? 'text' : 'password'}
                          error={!!errors.confirmPassword}
                          helperText={errors.confirmPassword?.message}
                          variant="outlined"
                          size="small"
                          sx={{ 
                            '& .MuiInputBase-root': { height: '36px' },
                            '& .MuiFormHelperText-root': { margin: '2px 0 0 0', fontSize: '0.75rem' }
                          }}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                  edge="end"
                                  size="small"
                                  sx={{ p: 0.5 }}
                                >
                                  {showConfirmPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                      />
                    )}
                  />
                  </Grid>
                </Grid>
                </Paper>
              </Grid>

              {/* Business Information Section (Seller Only) */}
              {userType === 'seller' && (
                <Grid item xs={12}>
                  <Paper 
                    elevation={1} 
                    sx={{ 
                      p: 2, 
                      mb: 0, 
                      border: '1px solid #e0e0e0',
                      backgroundColor: '#ffffff',
                      borderRadius: 1
                    }}
                  >
                  <Typography 
                    variant="subtitle1" 
                    sx={{ 
                      mb: 1.5, 
                      color: '#333333',
                      fontWeight: 600,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      fontSize: '1rem'
                    }}
                  >
                    <Business sx={{ fontSize: 18 }} />
                    Business Information
                  </Typography>
                  
                  <Grid container spacing={1.5}>
                    <Grid item xs={12} sm={6}>
                      <Controller
                        name="businessName"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="Business Name"
                            error={!!errors.businessName}
                            helperText={errors.businessName?.message}
                            variant="outlined"
                            size="small"
                            sx={{ 
                              '& .MuiInputBase-root': { height: '36px' },
                              '& .MuiFormHelperText-root': { margin: '2px 0 0 0', fontSize: '0.75rem' }
                            }}
                          />
                        )}
                      />
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <FormControl 
                        fullWidth 
                        error={!!errors.businessType}
                        size="small"
                        sx={{ 
                          '& .MuiInputBase-root': { height: '36px' },
                          '& .MuiFormHelperText-root': { margin: '2px 0 0 0', fontSize: '0.75rem' }
                        }}
                      >
                        <InputLabel>Business Type</InputLabel>
                        <Controller
                          name="businessType"
                          control={control}
                          render={({ field }) => (
                            <Select
                              {...field}
                              label="Business Type"
                              MenuProps={{
                                PaperProps: {
                                  style: {
                                    maxHeight: 200,
                                  },
                                },
                              }}
                              sx={{
                                minWidth: '200px',
                                '& .MuiSelect-select': {
                                  display: 'flex',
                                  alignItems: 'center',
                                  minHeight: 'unset',
                                  padding: '8px 14px',
                                }
                              }}
                            >
                              {businessTypes.map((type) => (
                                <MenuItem key={type} value={type}>
                                  {type}
                                </MenuItem>
                              ))}
                            </Select>
                          )}
                        />
                        {errors.businessType && (
                          <FormHelperText>{errors.businessType.message}</FormHelperText>
                        )}
                      </FormControl>
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <Controller
                        name="gstNumber"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="GST Number"
                            placeholder="Enter GST Number"
                            error={!!errors.gstNumber}
                            helperText={errors.gstNumber?.message}
                            variant="outlined"
                            size="small"
                            sx={{ 
                              '& .MuiInputBase-root': { height: '36px' },
                              '& .MuiFormHelperText-root': { margin: '2px 0 0 0', fontSize: '0.75rem' }
                            }}
                          />
                        )}
                      />
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <Controller
                        name="panNumber"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="PAN Number"
                            placeholder="Enter PAN Number"
                            error={!!errors.panNumber}
                            helperText={errors.panNumber?.message}
                            variant="outlined"
                            size="small"
                            sx={{ 
                              '& .MuiInputBase-root': { height: '36px' },
                              '& .MuiFormHelperText-root': { margin: '2px 0 0 0', fontSize: '0.75rem' }
                            }}
                          />
                        )}
                      />
                    </Grid>
                    
                    <Grid item xs={12}>
                      <Controller
                        name="businessAddress"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="Business Address"
                            multiline
                            rows={1}
                            error={!!errors.businessAddress}
                            helperText={errors.businessAddress?.message}
                            variant="outlined"
                            size="small"
                            sx={{ 
                              '& .MuiFormHelperText-root': { margin: '2px 0 0 0', fontSize: '0.75rem' }
                            }}
                          />
                        )}
                      />
                    </Grid>
                      
                      {/* City, State, Pincode */}
                      <Grid item xs={12} sm={4}>
                        <Controller
                          name="city"
                          control={control}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              fullWidth
                              label="City"
                              error={!!errors.city}
                              helperText={errors.city?.message}
                              variant="outlined"
                              size="small"
                              sx={{ 
                                '& .MuiInputBase-root': { height: '36px' },
                                '& .MuiFormHelperText-root': { margin: '2px 0 0 0', fontSize: '0.75rem' }
                              }}
                            />
                          )}
                        />
                      </Grid>
                      
                      <Grid item xs={12} sm={4}>
                        <Controller
                          name="state"
                          control={control}
                          render={({ field }) => {
                            const allStateOptions = [...filteredStates, CUSTOM_STATE_OPTION];
                            
                            return (
                              <Autocomplete
                                options={allStateOptions}
                                value={isCustomState ? CUSTOM_STATE_OPTION : (field.value || null)}
                                onChange={(event, newValue) => {
                                  if (newValue === CUSTOM_STATE_OPTION) {
                                    setIsCustomState(true);
                                    setCustomStateValue('');
                                    field.onChange('');
                                  } else if (newValue) {
                                    setIsCustomState(false);
                                    setCustomStateValue('');
                                    field.onChange(newValue);
                                  } else {
                                    setIsCustomState(false);
                                    setCustomStateValue('');
                                    field.onChange('');
                                  }
                                }}
                                onInputChange={(event, newInputValue) => {
                                  setStateSearchTerm(newInputValue);
                                }}
                                inputValue={stateSearchTerm}
                                getOptionLabel={(option) => {
                                  if (option === CUSTOM_STATE_OPTION) {
                                    return '+ Add Custom State';
                                  }
                                  return option || '';
                                }}
                                renderOption={(props, option) => {
                                  if (option === CUSTOM_STATE_OPTION) {
                                    return (
                                      <Box
                                        component="li"
                                        {...props}
                                        sx={{ fontStyle: 'italic', color: 'text.secondary' }}
                                      >
                                        + Add Custom State
                                      </Box>
                                    );
                                  }
                                  return (
                                    <Box component="li" {...props}>
                                      {option}
                                    </Box>
                                  );
                                }}
                                filterOptions={(options, params) => {
                                  const filtered = options.filter((option) => {
                                    if (option === CUSTOM_STATE_OPTION) return true;
                                    return option.toLowerCase().includes(params.inputValue.toLowerCase());
                                  });
                                  return filtered;
                                }}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    label="State"
                                    error={!!errors.state}
                                    helperText={errors.state?.message}
                                    size="small"
                                    InputProps={{
                                      ...params.InputProps,
                                      startAdornment: (
                                        <>
                                          <InputAdornment position="start">
                                            <Search fontSize="small" />
                                          </InputAdornment>
                                          {params.InputProps.startAdornment}
                                        </>
                                      ),
                                    }}
                                    sx={{ 
                                      '& .MuiInputBase-root': { height: '36px' },
                                      '& .MuiFormHelperText-root': { margin: '2px 0 0 0', fontSize: '0.75rem' },
                                      minWidth: '200px'
                                    }}
                                  />
                                )}
                                sx={{ width: '100%', minWidth: '200px' }}
                                noOptionsText="No state found"
                                clearOnEscape
                                selectOnFocus
                                handleHomeEndKeys
                              />
                            );
                          }}
                        />
                      </Grid>
                      
                      {/* Custom State Input */}
                      {isCustomState && (
                        <Grid item xs={12} sm={4}>
                          <TextField
                            fullWidth
                            label="Custom State"
                            value={customStateValue}
                            onChange={(e) => {
                              const value = e.target.value;
                              setCustomStateValue(value);
                              setValue('state', value, { shouldValidate: true });
                            }}
                            error={!!errors.state}
                            helperText={errors.state?.message || 'Enter your custom state'}
                            variant="outlined"
                            size="small"
                            sx={{ 
                              '& .MuiInputBase-root': { height: '36px' },
                              '& .MuiFormHelperText-root': { margin: '2px 0 0 0', fontSize: '0.75rem' }
                            }}
                          />
                        </Grid>
                      )}
                      
                      {/* Country Field */}
                      <Grid item xs={12} sm={4}>
                        <Controller
                          name="country"
                          control={control}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              fullWidth
                              label="Country"
                              error={!!errors.country}
                              helperText={errors.country?.message}
                              variant="outlined"
                              size="small"
                              placeholder="e.g., India"
                              sx={{ 
                                '& .MuiInputBase-root': { height: '36px' },
                                '& .MuiFormHelperText-root': { margin: '2px 0 0 0', fontSize: '0.75rem' }
                              }}
                            />
                          )}
                        />
                      </Grid>
                      
                      <Grid item xs={12} sm={4}>
                        <Controller
                          name="pincode"
                          control={control}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              fullWidth
                              label="Pincode"
                              error={!!errors.pincode}
                              helperText={errors.pincode?.message}
                              variant="outlined"
                              size="small"
                              placeholder="e.g., 110001"
                              sx={{ 
                                '& .MuiInputBase-root': { height: '36px' },
                                '& .MuiFormHelperText-root': { margin: '2px 0 0 0', fontSize: '0.75rem' }
                              }}
                            />
                          )}
                        />
                      </Grid>
                      
                      {/* Bank Account Details */}
                      <Grid item xs={12} sm={4}>
                        <Controller
                          name="bankAccountName"
                          control={control}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              fullWidth
                              label="Bank Account Name (Optional)"
                              placeholder="Enter account holder name"
                              error={!!errors.bankAccountName}
                              helperText={errors.bankAccountName?.message}
                              variant="outlined"
                              size="small"
                              sx={{ 
                                '& .MuiInputBase-root': { height: '36px' },
                                '& .MuiFormHelperText-root': { margin: '2px 0 0 0', fontSize: '0.75rem' }
                              }}
                            />
                          )}
                        />
                      </Grid>
                      
                      <Grid item xs={12} sm={4}>
                        <Controller
                          name="bankAccountNumber"
                          control={control}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              fullWidth
                              label="Bank Account Number (Optional)"
                              placeholder="Enter account number"
                              error={!!errors.bankAccountNumber}
                              helperText={errors.bankAccountNumber?.message}
                              variant="outlined"
                              size="small"
                              sx={{ 
                                '& .MuiInputBase-root': { height: '36px' },
                                '& .MuiFormHelperText-root': { margin: '2px 0 0 0', fontSize: '0.75rem' }
                              }}
                            />
                          )}
                        />
                      </Grid>
                      
                      <Grid item xs={12} sm={4}>
                        <Controller
                          name="ifscCode"
                          control={control}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              fullWidth
                              label="IFSC Code (Optional)"
                              placeholder="e.g., SBIN0001234"
                              error={!!errors.ifscCode}
                              helperText={errors.ifscCode?.message}
                              variant="outlined"
                              size="small"
                              inputProps={{ style: { textTransform: 'uppercase' } }}
                              sx={{ 
                                '& .MuiInputBase-root': { height: '36px' },
                                '& .MuiFormHelperText-root': { margin: '2px 0 0 0', fontSize: '0.75rem' }
                              }}
                            />
                          )}
                        />
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>
              )}
              
              {/* Agreement Section */}
              <Grid item xs={12}>
                <Paper 
                  elevation={1} 
                  sx={{ 
                    p: 2, 
                    mb: 0, 
                    border: '1px solid #e0e0e0',
                    backgroundColor: '#ffffff',
                    borderRadius: 1
                  }}
                >
                  <Typography 
                    variant="subtitle1" 
                    sx={{ 
                      mb: 1.5, 
                      color: '#333333',
                      fontWeight: 600,
                      fontSize: '1rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }}
                  >
                    <Description fontSize="18" />
                    Agreement
                  </Typography>
                  
                  <Controller
                    name="agreeToTerms"
                    control={control}
                    render={({ field }) => (
                      <FormControlLabel
                        control={
                          <Checkbox
                            {...field}
                            checked={field.value}
                            color="primary"
                            size="small"
                            sx={{ p: 0.5 }}
                          />
                        }
                        sx={{ 
                          alignItems: 'flex-start',
                          mt: 0.5,
                          '& .MuiFormControlLabel-label': {
                            mt: 0.25
                          }
                        }}
                        label={
                          <Typography variant="body2" sx={{ lineHeight: 1.5, fontSize: '0.875rem' }}>
                            I agree to the{' '}
                            <Link 
                              component={RouterLink} 
                              to="/terms" 
                              target="_blank"
                              sx={{ 
                                fontWeight: 600,
                                textDecoration: 'none',
                                '&:hover': { textDecoration: 'underline' }
                              }}
                            >
                              Terms of Service
                            </Link>{' '}
                            and{' '}
                            <Link 
                              component={RouterLink} 
                              to="/privacy" 
                              target="_blank"
                              sx={{ 
                                fontWeight: 600,
                                textDecoration: 'none',
                                '&:hover': { textDecoration: 'underline' }
                              }}
                            >
                              Privacy Policy
                            </Link>
                            {userType === 'seller' && (
                              <>
                                {' '}and{' '}
                                <Link 
                                  component={RouterLink} 
                                  to="/seller-terms" 
                                  target="_blank"
                                  sx={{ 
                                    fontWeight: 600,
                                    textDecoration: 'none',
                                    '&:hover': { textDecoration: 'underline' }
                                  }}
                                >
                                  Seller Agreement
                                </Link>
                              </>
                            )}
                          </Typography>
                        }
                      />
                    )}
                  />
                  {errors.agreeToTerms && (
                    <Typography variant="body2" color="error" sx={{ mt: 0.5, ml: 3.5, fontSize: '0.75rem' }}>
                      {errors.agreeToTerms.message}
                    </Typography>
                  )}
                </Paper>
              </Grid>
              
              {/* Submit Button */}
              <Grid item xs={12}>
                {!emailVerified && (
                  <Alert severity="warning" sx={{ mb: 1, fontSize: '0.82rem' }}>
                    Please verify your email address to enable registration.
                  </Alert>
                )}
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="medium"
                  disabled={loading || !emailVerified}
                  sx={{
                    py: 1.25,
                    fontSize: '1rem',
                    fontWeight: 600,
                    height: '44px',
                    background: userType === 'seller'
                      ? '#2E3C50'
                      : 'linear-gradient(45deg, #2C3E50, #34495E)',
                    '&:hover': {
                      background: userType === 'seller'
                        ? '#1A252F'
                        : 'linear-gradient(45deg, #1A252F, #2C3E50)',
                    },
                    '&.Mui-disabled': {
                      background: '#ccc',
                      color: '#666',
                    },
                  }}
                >
                  {loading ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    `Register as ${userType === 'seller' ? 'Seller' : 'Customer'}`
                  )}
                </Button>
              </Grid>
            </Grid>
          </Box>
          
          {/* Login Link */}
          <Box sx={{ mt: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Already have an account?{' '}
              <Link component={RouterLink} to="/login" sx={{ textDecoration: 'none' }}>
                Sign in here
              </Link>
            </Typography>
          </Box>
        </Paper>
      </motion.div>
    </Container>
  );
};

export default RegisterPage;